import { useEffect, useState, type FormEvent } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { refreshCmsFeed } from '../cms/store'
import { MAX_UPLOAD_BYTES, SOCIAL_FIELDS } from '../lib/cms'
import { mediaUrl } from '../lib/media'
import { api, apiUpload, type AdminAuthor } from './api'
import { refreshAdminAuthors } from './authorsStore'
import FilePicker from './FilePicker'

const inputClass = 'w-full border border-rule bg-paper px-3 py-2.5 text-[15px] outline-none focus:border-ink'
const labelClass = 'text-caps mb-1.5 block text-[10px] text-muted'

type SocialDraft = Record<(typeof SOCIAL_FIELDS)[number]['type'], string>

const emptySocials: SocialDraft = {
  instagram: '',
  facebook: '',
  tiktok: '',
  pinterest: '',
}

function socialsFromPerson(socials: { type: string; url: string }[]) {
  const next = { ...emptySocials }
  for (const social of socials) {
    if (social.type in next) next[social.type as keyof SocialDraft] = social.url
  }
  return next
}

function socialsPayload(fields: SocialDraft, extra: { type: string; url: string }[]) {
  const named = SOCIAL_FIELDS.map(({ type }) => ({ type, url: fields[type].trim() })).filter((social) => social.url)
  return [...named, ...extra.filter((social) => social.url.trim() && !(social.type in emptySocials))]
}

const empty = {
  name: '',
  bio: '',
  email: '',
  website: '',
  photo: '',
  socials: emptySocials,
  extraSocials: [] as { type: string; url: string }[],
}

export default function AdminContributorForm() {
  const { id } = useParams()
  const navigate = useNavigate()
  const isNew = !id
  const [form, setForm] = useState(empty)
  const [error, setError] = useState('')
  const [busy, setBusy] = useState(false)
  const [ready, setReady] = useState(isNew)

  useEffect(() => {
    if (isNew || !id) return
    api<AdminAuthor>(`/api/admin/authors/${id}`)
      .then((person) => {
        setForm({
          name: person.name,
          bio: person.bio,
          email: person.email || '',
          website: person.website || '',
          photo: person.photo || '',
          socials: socialsFromPerson(person.socials),
          extraSocials: person.socials.filter((social) => !(social.type in emptySocials)),
        })
        setReady(true)
      })
      .catch((err: Error) => setError(err.message))
  }, [id, isNew])

  function patch<K extends keyof typeof form>(key: K, value: (typeof form)[K]) {
    setForm((prev) => ({ ...prev, [key]: value }))
  }

  async function onAvatar(file: File) {
    if (file.size > MAX_UPLOAD_BYTES) {
      setError('Images must be 5MB or smaller.')
      return
    }
    const uploaded = await apiUpload(file, 'author')
    patch('photo', uploaded.key)
  }

  async function onSubmit(event: FormEvent) {
    event.preventDefault()
    setBusy(true)
    setError('')
    const payload = {
      name: form.name,
      bio: form.bio,
      email: form.email,
      website: form.website,
      photo: form.photo,
      socials: socialsPayload(form.socials, form.extraSocials),
    }
    try {
      if (isNew) {
        const created = await api<AdminAuthor>('/api/admin/authors', { method: 'POST', body: JSON.stringify(payload) })
        await refreshAdminAuthors()
        await refreshCmsFeed()
        navigate(`/admin/contributors/${created.id}`, { replace: true })
      } else {
        await api(`/api/admin/authors/${id}`, { method: 'PUT', body: JSON.stringify(payload) })
        await refreshAdminAuthors()
        await refreshCmsFeed()
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save.')
    } finally {
      setBusy(false)
    }
  }

  async function onDelete() {
    if (!id || !window.confirm('Remove this contributor?')) return
    await api(`/api/admin/authors/${id}`, { method: 'DELETE' })
    await refreshAdminAuthors()
    await refreshCmsFeed()
    navigate('/admin/contributors')
  }

  if (!ready && !error) return <p className="text-muted">Loading…</p>

  return (
    <form onSubmit={(event) => void onSubmit(event)} className="mx-auto max-w-[640px]">
      <div className="mb-6 flex items-center justify-between gap-4">
        <h2 className="font-serif text-[32px] leading-none font-medium">{isNew ? 'New contributor' : 'Edit contributor'}</h2>
        <Link to="/admin/contributors" className="text-[11px] tracking-[0.16em] text-muted uppercase hover:text-rose">
          All contributors
        </Link>
      </div>
      {error ? <p className="mb-4 text-sm text-rose">{error}</p> : null}
      <label className="block">
        <span className={labelClass}>Name</span>
        <input required value={form.name} onChange={(event) => patch('name', event.target.value)} className={inputClass} />
      </label>
      <div className="mt-5">
        <span className={labelClass}>Avatar</span>
        {form.photo ? (
          <img src={mediaUrl(form.photo, 'thumb')} alt="" className="mb-3 size-28 rounded-full object-cover object-top" />
        ) : null}
        <FilePicker label={form.photo ? 'Replace photo' : 'Choose file'} onFile={(file) => void onAvatar(file)} />
        <p className="mt-2 text-[12px] text-muted">5MB max.</p>
      </div>
      <label className="mt-5 block">
        <span className={labelClass}>Bio</span>
        <textarea rows={6} value={form.bio} onChange={(event) => patch('bio', event.target.value)} className={inputClass} />
      </label>
      <label className="mt-5 block">
        <span className={labelClass}>Email</span>
        <input type="email" value={form.email} onChange={(event) => patch('email', event.target.value)} className={inputClass} />
      </label>
      <label className="mt-5 block">
        <span className={labelClass}>Website</span>
        <input value={form.website} onChange={(event) => patch('website', event.target.value)} className={inputClass} />
      </label>
      <div className="mt-5">
        <span className={labelClass}>Socials</span>
        <div className="flex flex-col gap-3">
          {SOCIAL_FIELDS.map((field) => (
            <label key={field.type} className="block">
              <span className="mb-1.5 block text-[12px] text-copy">{field.label}</span>
              <input
                value={form.socials[field.type]}
                placeholder="https://"
                onChange={(event) => patch('socials', { ...form.socials, [field.type]: event.target.value })}
                className={inputClass}
              />
            </label>
          ))}
        </div>
      </div>
      <button type="submit" disabled={busy} className="text-caps mt-8 h-12 w-full bg-ink text-white hover:bg-rose disabled:opacity-60">
        {busy ? 'Saving' : 'Save contributor'}
      </button>
      {!isNew ? (
        <button type="button" onClick={() => void onDelete()} className="mt-4 text-[11px] tracking-[0.16em] text-muted uppercase hover:text-rose">
          Delete
        </button>
      ) : null}
    </form>
  )
}
